import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { useApi } from './api';
import type { ApiResponse } from '../types';

/**
 * Connexion du compte Claude, en un clic.
 *
 * Le parcours tient en trois temps : ouvrir une session côté serveur, autoriser
 * dans un onglet, recopier le code obtenu. Il est ici plutôt que dans un écran
 * parce qu'il est offert à deux endroits, la fenêtre d'accueil et l'écran
 * Admin, et que deux copies auraient divergé.
 *
 * Le jeton ne passe jamais par le navigateur : il va du courtier à la base,
 * chiffré. Rien de ce qui transite ici n'en contient.
 */

/** Ce que le serveur a répondu en essayant le jeton juste enregistré. */
export interface Verification {
    ok: boolean;
    error?: string;
    model?: string;
}

export interface ClaudeConnection {
    /** Session ouverte : l'URL à autoriser et de quoi transmettre le code. */
    session: { sessionId: string; url: string } | null;
    code: string;
    setCode: (code: string) => void;
    error: string | null;
    /**
     * Résultat de l'essai du jeton. Un jeton peut être enregistré et refusé :
     * l'écran doit le dire, plutôt que de laisser la première génération
     * l'apprendre.
     */
    verification: Verification | null;
    starting: boolean;
    submitting: boolean;
    /**
     * L'essai est en cours.
     *
     * Il est distingué de l'enregistrement parce qu'il lance une vraie
     * génération minuscule et dure bien plus longtemps. Les confondre figeait
     * le bouton « Valider » pendant une minute, sans qu'on sache si le code
     * avait été accepté.
     */
    verifying: boolean;
    /** Ouvre la session et tente d'ouvrir l'onglet d'autorisation. */
    start: () => void;
    /** Transmet le code saisi. */
    submit: () => void;
    /** Essaie le jeton déjà enregistré, sans repasser par l'autorisation. */
    verify: () => void;
    /** Abandonne la session en cours et revient à l'état initial. */
    cancel: () => void;
}

export function useClaudeConnection(onConnected?: () => void): ClaudeConnection {
    const api = useApi();
    const [session, setSession] = useState<{ sessionId: string; url: string } | null>(null);
    const [code, setCode] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [verification, setVerification] = useState<Verification | null>(null);

    const startMutation = useMutation({
        mutationFn: () => api.post<ApiResponse<{ sessionId: string; url: string }>>(
            '/api/admin/ai-settings/credentials/claude_code/connect',
        ),
        onSuccess: (response) => {
            setError(null);
            setSession(response.data);
            // L'ouverture ne suit pas un geste direct, l'appel au courtier
            // s'étant intercalé : un bloqueur de fenêtres la refuse sans rien
            // signaler. Le bouton « Ouvrir l'autorisation » reste donc affiché,
            // et c'est lui qui fait foi.
            window.open(response.data.url, '_blank', 'noopener');
        },
        onError: (e: any) => setError(e?.message || 'Connexion impossible'),
    });

    const verifyMutation = useMutation({
        mutationFn: () => api.post<ApiResponse<Verification>>(
            '/api/admin/ai-settings/credentials/claude_code/verify',
        ),
        onSuccess: (response) => setVerification(response.data),
        // Un essai qui échoue pour une raison passagère ne doit pas laisser
        // croire que le jeton est mauvais : on dit ce qu'on sait.
        onError: (e: any) => setVerification({
            ok: false,
            error: e?.message || "L'essai du jeton n'a pas abouti. Réessayez « Tester la connexion ».",
        }),
    });

    const submitMutation = useMutation({
        mutationFn: () => api.post(
            '/api/admin/ai-settings/credentials/claude_code/complete',
            { sessionId: session?.sessionId, code: code.trim() },
        ),
        onSuccess: () => {
            setSession(null);
            setCode('');
            setError(null);
            setVerification(null);
            onConnected?.();
            // L'essai suit l'enregistrement au lieu d'en faire partie : la
            // saisie du code se conclut tout de suite, et l'écran annonce
            // ensuite le résultat de l'essai.
            verifyMutation.mutate();
        },
        onError: (e: any) => setError(e?.message || 'Code refusé'),
    });

    return {
        session,
        code,
        setCode,
        error,
        verification,
        starting: startMutation.isPending,
        submitting: submitMutation.isPending,
        verifying: verifyMutation.isPending,
        // Reprendre la connexion efface ce qui restait du dernier essai :
        // laisser un ancien refus affiché pendant qu'on en refait une donne à
        // croire qu'il porte sur la tentative en cours.
        start: () => { setVerification(null); setError(null); startMutation.mutate(); },
        verify: () => { setError(null); verifyMutation.mutate(); },
        submit: () => submitMutation.mutate(),
        cancel: () => { setSession(null); setCode(''); setError(null); },
    };
}
